import { ChainParameters } from '@/types';

/**
 * Get the appropriate API base URL based on environment
 */
function getApiBaseUrl(): string {
  // In development, use proxy to avoid CORS issues
  if (import.meta.env?.DEV) {
    return '/api/tron';
  }
  return 'https://api.trongrid.io';
}

const DEFAULT_ENDPOINT = getApiBaseUrl();

/**
 * Fetch chain parameters from TRON network
 */
export async function fetchChainParameters(
  endpoint: string = DEFAULT_ENDPOINT,
  apiKey?: string
): Promise<ChainParameters> {
  try {
    const url = `${endpoint}/wallet/getchainparameters`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (apiKey) {
      headers['TRON-PRO-API-KEY'] = apiKey;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Extract energy and bandwidth unit prices
    const energyFee = data.chainParameter?.find((param: any) => param.key === 'getEnergyFee');
    const transactionFee = data.chainParameter?.find((param: any) => param.key === 'getTransactionFee');

    if (!energyFee || !transactionFee) {
      throw new Error('Required chain parameters not found');
    }

    return {
      energyUnitSun: parseInt(energyFee.value),
      bandwidthUnitSun: parseInt(transactionFee.value),
    };
  } catch (error) {
    console.error('Failed to fetch chain parameters:', error);
    
    // Return fallback values if API fails
    return {
      energyUnitSun: 420, // Default fallback value
      bandwidthUnitSun: 1000, // Default fallback value (not 0)
    };
  }
}

/**
 * Check if the TRON endpoint is accessible
 */
export async function checkEndpointHealth(endpoint: string = DEFAULT_ENDPOINT): Promise<boolean> {
  try {
    const response = await fetch(`${endpoint}/wallet/getchainparameters`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
    
    return response.ok;
  } catch (error) {
    console.error('Endpoint health check failed:', error);
    return false;
  }
}

/**
 * Get cached chain parameters from localStorage
 */
export function getCachedChainParameters(): ChainParameters | null {
  try {
    const cached = localStorage.getItem('tron-chain-parameters');
    if (cached) {
      const data = JSON.parse(cached);
      // Check if cache is less than 1 hour old
      if (Date.now() - data.timestamp < 60 * 60 * 1000) {
        return data.parameters;
      }
    }
    return null;
  } catch (error) {
    console.error('Failed to read cached chain parameters:', error);
    return null;
  }
}

/**
 * Cache chain parameters in localStorage
 */
export function cacheChainParameters(parameters: ChainParameters): void {
  try {
    const cacheData = {
      parameters,
      timestamp: Date.now(),
    };
    localStorage.setItem('tron-chain-parameters', JSON.stringify(cacheData));
  } catch (error) {
    console.error('Failed to cache chain parameters:', error);
  }
}
